// FieldDesk — экран «Настройки» (только SUPER_ADMIN).
// Четыре секции:
//   1. Интеграция с Битрикс24 — воронка, отдел, sync users.
//   2. Нагрузка инженеров — пороги раскраски графика в «Отчётах».
//   3. Управление пользователями — таблица с изменением ролей.
//   4. Типы выездов — read-only, заглушка.

import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import type { AdminSettings, AdminUserRow, BitrixSelectItem, UserRole } from '../api/types';

const ROLE_COLOR: Record<UserRole, string> = {
  ADMIN: '#dc2626',
  DISPATCHER: '#2563eb',
  ENGINEER: '#16a34a',
};

const ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: 'Admin',
  DISPATCHER: 'Dispatcher',
  ENGINEER: 'Engineer',
};

const ACTIVATION_LABEL: Record<string, string> = {
  INVITED: 'Приглашён',
  ACTIVE: 'Активен',
  DISABLED: 'Отключён',
};

export function SettingsScreen() {
  // reloadTick позволяет BitrixSection сигнализировать UsersSection о перезагрузке.
  const [usersTick, setUsersTick] = useState(0);

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '24px 32px', background: '#f8fafc' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 28 }}>
        ⚙ Настройки
      </h1>
      <BitrixSection onUsersReload={() => setUsersTick((t) => t + 1)} />
      <WorkloadSection />
      <UsersSection reloadTick={usersTick} />
      <VisitTypesSection />
    </div>
  );
}

// =============================================================================
// Секция 1 — Интеграция с Битрикс24
// =============================================================================
function BitrixSection({ onUsersReload }: { onUsersReload(): void }) {
  const { api } = useAuth();
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [categories, setCategories] = useState<BitrixSelectItem[]>([]);
  const [departments, setDepartments] = useState<BitrixSelectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<Record<string, 'idle' | 'saving' | 'ok' | 'err'>>({});
  const [err, setErr] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [superAdminIds, setSuperAdminIds] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, cats, depts] = await Promise.all([
        api.adminGetSettings(),
        api.adminGetDealCategories().catch(() => ({ items: [] as BitrixSelectItem[] })),
        api.adminGetDepartments().catch(() => ({ items: [] as BitrixSelectItem[] })),
      ]);
      setSettings(s);
      setCategories(cats.items);
      setDepartments(depts.items);
      setSelectedCategory(s.bitrixDealCategoryId ?? '');
      setSelectedDept(s.bitrixOvisDepartmentId ?? '');
      setSuperAdminIds(s.superAdminBitrixIds ?? '');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  async function save(field: string, value: string) {
    setSaveStatus((s) => ({ ...s, [field]: 'saving' }));
    try {
      const updated = await api.adminPatchSettings({ [field]: value });
      setSettings(updated);
      setSaveStatus((s) => ({ ...s, [field]: 'ok' }));
      setTimeout(() => setSaveStatus((s) => ({ ...s, [field]: 'idle' })), 2000);
    } catch (e) {
      setSaveStatus((s) => ({ ...s, [field]: 'err' }));
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  async function handleSync() {
    setSyncMsg('Синхронизация…');
    try {
      const r = await api.syncUsersFromBitrix();
      setSyncMsg(`Готово: ${JSON.stringify(r.report)}`);
    } catch (e) {
      setSyncMsg(`Ошибка: ${(e as Error).message}`);
    }
    setTimeout(() => setSyncMsg(null), 6000);
  }

  async function handleSyncAll() {
    setSyncMsg('Загрузка всех из Б24…');
    try {
      const r = await api.adminSyncAllBitrixUsers();
      setSyncMsg(`Добавлено: ${r.added}, пропущено: ${r.skipped}, всего в Б24: ${r.total}`);
      onUsersReload();
    } catch (e) {
      setSyncMsg(`Ошибка: ${(e as Error).message}`);
    }
    setTimeout(() => setSyncMsg(null), 8000);
  }

  return (
    <Section title="Интеграция с Битрикс24">
      {err && <ErrMsg msg={err} />}
      {loading ? (
        <Muted>Загрузка…</Muted>
      ) : (
        <>
          <FieldRow label="Воронка сделок">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={selectStyle}
            >
              <option value="">— не выбрано —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <SaveBtn
              status={saveStatus['bitrixDealCategoryId'] ?? 'idle'}
              onClick={() => save('bitrixDealCategoryId', selectedCategory)}
            />
          </FieldRow>

          <FieldRow label="Отдел ОВиС">
            <select
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
              style={selectStyle}
            >
              <option value="">— не выбрано —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <SaveBtn
              status={saveStatus['bitrixOvisDepartmentId'] ?? 'idle'}
              onClick={() => save('bitrixOvisDepartmentId', selectedDept)}
            />
          </FieldRow>

          <FieldRow label="Super admin IDs (через запятую)">
            <input
              value={superAdminIds}
              onChange={(e) => setSuperAdminIds(e.target.value)}
              placeholder="9,217"
              style={{ ...inputStyle, width: 200 }}
            />
            <SaveBtn
              status={saveStatus['superAdminBitrixIds'] ?? 'idle'}
              onClick={() => save('superAdminBitrixIds', superAdminIds)}
            />
          </FieldRow>

          {settings?.updatedAt && (
            <Muted style={{ marginTop: 8 }}>
              Обновлено: {new Date(settings.updatedAt).toLocaleString('ru')}
            </Muted>
          )}

          <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={handleSync} style={actionBtnStyle}>
              🔄 Sync users из Б24
            </button>
            <button onClick={handleSyncAll} style={{ ...actionBtnStyle, background: '#0369a1' }}>
              📥 Загрузить всех из Б24
            </button>
            {syncMsg && <span style={{ fontSize: 12, color: '#475569' }}>{syncMsg}</span>}
          </div>
        </>
      )}
    </Section>
  );
}

// =============================================================================
// Секция 2 — Пороги нагрузки инженеров (график в «Отчётах»)
// =============================================================================
type WorkloadKey = 'workloadWeekWarn' | 'workloadWeekHigh' | 'workloadMonthWarn' | 'workloadMonthHigh';

function WorkloadSection() {
  const { api } = useAuth();
  const [values, setValues] = useState<Record<WorkloadKey, string> | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'ok' | 'err'>('idle');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .adminGetSettings()
      .then((s) =>
        setValues({
          workloadWeekWarn: String(s.workloadWeekWarn),
          workloadWeekHigh: String(s.workloadWeekHigh),
          workloadMonthWarn: String(s.workloadMonthWarn),
          workloadMonthHigh: String(s.workloadMonthHigh),
        }),
      )
      .catch((e) => setErr(e instanceof ApiError ? e.message : (e as Error).message));
  }, [api]);

  async function handleSave() {
    if (!values) return;
    const nums = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, Number(v)]),
    ) as Record<WorkloadKey, number>;
    if (Object.values(nums).some((n) => !Number.isInteger(n) || n < 1)) {
      setErr('Пороги должны быть целыми числами от 1');
      return;
    }
    if (nums.workloadWeekWarn > nums.workloadWeekHigh || nums.workloadMonthWarn > nums.workloadMonthHigh) {
      setErr('Порог «перегруз» должен быть не меньше порога «высокая нагрузка»');
      return;
    }
    setErr(null);
    setStatus('saving');
    try {
      await api.adminPatchSettings(nums);
      setStatus('ok');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (e) {
      setStatus('err');
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  function input(key: WorkloadKey) {
    return (
      <input
        type="number"
        min={1}
        value={values?.[key] ?? ''}
        onChange={(e) => setValues((v) => (v ? { ...v, [key]: e.target.value } : v))}
        style={{ ...inputStyle, width: 70 }}
      />
    );
  }

  return (
    <Section title="Нагрузка инженеров">
      <Muted style={{ marginBottom: 14 }}>
        Цвет полосы на графике «Нагрузка инженеров» в «Отчётах» зависит от числа выездов за период.
        Отменённые выезды не считаются.
      </Muted>
      {err && <ErrMsg msg={err} />}
      {!values ? (
        <Muted>Загрузка…</Muted>
      ) : (
        <>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, marginBottom: 14 }}>
            <thead>
              <tr>
                <th style={thStyle}>Период</th>
                <th style={thStyle}>🟡 Высокая нагрузка — от</th>
                <th style={thStyle}>🔴 Перегруз — больше</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>Неделя</td>
                <td style={tdStyle}>{input('workloadWeekWarn')}</td>
                <td style={tdStyle}>{input('workloadWeekHigh')}</td>
              </tr>
              <tr>
                <td style={tdStyle}>Месяц</td>
                <td style={tdStyle}>{input('workloadMonthWarn')}</td>
                <td style={tdStyle}>{input('workloadMonthHigh')}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SaveBtn status={status} onClick={() => void handleSave()} />
            <Muted>
              Пример для недели: меньше {values.workloadWeekWarn || '…'} — 🟢, от {values.workloadWeekWarn || '…'} до{' '}
              {values.workloadWeekHigh || '…'} — 🟡, больше {values.workloadWeekHigh || '…'} — 🔴
            </Muted>
          </div>
        </>
      )}
    </Section>
  );
}

// =============================================================================
// Секция 3 — Управление пользователями
// =============================================================================
function UsersSection({ reloadTick }: { reloadTick: number }) {
  const { api } = useAuth();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [roleChanging, setRoleChanging] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { items } = await api.adminListUsers();
      setUsers(items);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load, reloadTick]);

  async function changeRole(userId: number, role: UserRole) {
    setRoleChanging((s) => ({ ...s, [userId]: true }));
    try {
      const updated = await api.adminSetUserRole(userId, role);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, ...updated } : u));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setRoleChanging((s) => ({ ...s, [userId]: false }));
    }
  }

  return (
    <Section title="Управление пользователями">
      {err && <ErrMsg msg={err} />}
      {loading ? (
        <Muted>Загрузка…</Muted>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                {['Имя', 'Bitrix ID', 'Роль', 'Статус'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={tdStyle}>{u.fullName}</td>
                  <td style={{ ...tdStyle, color: '#64748b', fontFamily: 'monospace', fontSize: 11 }}>
                    {u.bitrixUserId ?? '—'}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: ROLE_COLOR[u.role],
                          flexShrink: 0,
                        }}
                      />
                      <select
                        value={u.role}
                        disabled={roleChanging[u.id]}
                        onChange={(e) => void changeRole(u.id, e.target.value as UserRole)}
                        style={{
                          ...selectStyle,
                          fontSize: 12,
                          padding: '3px 6px',
                          color: ROLE_COLOR[u.role],
                          fontWeight: 600,
                        }}
                      >
                        {(['ADMIN', 'DISPATCHER', 'ENGINEER'] as UserRole[]).map((r) => (
                          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                        ))}
                      </select>
                      {roleChanging[u.id] && <Spinner />}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 600,
                        background:
                          u.activationStatus === 'ACTIVE'
                            ? '#dcfce7'
                            : u.activationStatus === 'DISABLED'
                              ? '#fef2f2'
                              : '#fefce8',
                        color:
                          u.activationStatus === 'ACTIVE'
                            ? '#15803d'
                            : u.activationStatus === 'DISABLED'
                              ? '#b91c1c'
                              : '#92400e',
                      }}
                    >
                      {ACTIVATION_LABEL[u.activationStatus] ?? u.activationStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <Muted style={{ marginTop: 12 }}>Пользователей нет.</Muted>}
        </div>
      )}
    </Section>
  );
}

// =============================================================================
// Секция 4 — Типы выездов (read-only, на будущее)
// =============================================================================
function VisitTypesSection() {
  const types = ['Установка', 'Обучение', 'Платный выезд'];
  return (
    <Section title="Типы выездов">
      <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 13, color: '#374151' }}>
        {types.map((t) => <li key={t} style={{ marginBottom: 4 }}>{t}</li>)}
      </ul>
      <Muted style={{ marginTop: 10 }}>
        Управление типами выездов будет добавлено позже.
      </Muted>
    </Section>
  );
}

// =============================================================================
// UI helpers
// =============================================================================
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        padding: '20px 24px',
        marginBottom: 20,
      }}
    >
      <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>{title}</h2>
      {children}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <span style={{ fontSize: 12, color: '#475569', minWidth: 200 }}>{label}</span>
      {children}
    </div>
  );
}

function SaveBtn({ status, onClick }: { status: 'idle' | 'saving' | 'ok' | 'err'; onClick(): void }) {
  const label = status === 'saving' ? '…' : status === 'ok' ? '✓' : status === 'err' ? '✗' : 'Сохранить';
  const bg = status === 'ok' ? '#16a34a' : status === 'err' ? '#dc2626' : '#2563eb';
  return (
    <button
      onClick={onClick}
      disabled={status === 'saving'}
      style={{
        padding: '5px 12px',
        background: bg,
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        minWidth: 72,
      }}
    >
      {label}
    </button>
  );
}

function ErrMsg({ msg }: { msg: string }) {
  return (
    <div style={{ padding: '8px 12px', background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
      {msg}
    </div>
  );
}

function Muted({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ fontSize: 12, color: '#94a3b8', ...style }}>{children}</div>;
}

function Spinner() {
  return <span style={{ fontSize: 11, color: '#94a3b8' }}>…</span>;
}

const selectStyle: CSSProperties = {
  padding: '5px 8px',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 13,
  background: '#fff',
  color: '#0f172a',
};

const inputStyle: CSSProperties = {
  padding: '5px 8px',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 13,
  background: '#fff',
  color: '#0f172a',
};

const actionBtnStyle: CSSProperties = {
  padding: '7px 14px',
  background: '#0f172a',
  color: '#e2e8f0',
  border: 'none',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const thStyle: CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle: CSSProperties = {
  padding: '8px 12px',
  verticalAlign: 'middle',
};
